import Page from "components/page/Page";
import React, { useState } from "react";
import { useWebsite } from "../shared/hooks/useWebsite";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useHistory } from "react-router";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import Card from "components/card/Card";
import "./websiteBuilderLogoAndBrand.scss";
import { DeleteWebsiteBuilderWebsiteImage, UpdateWebsiteBuilderWebsite } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { TWebsiteBuilderImageDimensionValidation, TWebsiteBuilderFileType, WebsiteBuilderFileDropZone, IWebsiteBuilderFilePreview } from "../shared/components/fileDropZone/WebsiteBuilderFileDropZone";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";

type TWebsiteBuilderLogoAndBrandImageType = "logo" | "favicon";

interface ILogoAndBrandUploadCard {
  title: string;
  description: string;
  imageType: TWebsiteBuilderLogoAndBrandImageType;
  previewUrl: string;
  url: string;
  allowedFileTypes: TWebsiteBuilderFileType[];
  maxImageMegabytes: number;
  imageDimensionValidations?: TWebsiteBuilderImageDimensionValidation[];
  filePreview: IWebsiteBuilderFilePreview;
  setFilePreview: (updatedFilePreview: IWebsiteBuilderFilePreview) => void;
}

export default function WebsiteBuilderLogoAndBrand() {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();
  
  const history = useHistory();

  const {
    website,
    websiteExists,
    websiteLoadingStatus,
    setWebsite,
  } = useWebsite({ websiteMustExist: true });

  const [logoFilePreview, setLogoFilePreview] = useState<IWebsiteBuilderFilePreview>(null);
  const [faviconFilePreview, setFaviconFilePreview] = useState<IWebsiteBuilderFilePreview>(null);

  const { state: deleteImageModal, updateModal: updateDeleteImageModal, closeModal: closeDeleteImageModal } = useModal<{
    imageType: TWebsiteBuilderLogoAndBrandImageType;
  }>(
    { imageType: null },
  );

  const changesExist = !!logoFilePreview || !!faviconFilePreview;

  const uploadCards: ILogoAndBrandUploadCard[] = [
    {
      title: t("Upload logo"),
      description: t("Customize your company logo"),
      imageType: "logo",
      previewUrl: logoFilePreview?.url,
      url: website?.logo_url,
      maxImageMegabytes: 0.5,
      allowedFileTypes: ["png", "svg"],
      filePreview: logoFilePreview,
      setFilePreview: setLogoFilePreview,
    },
    {
      title: t("Upload favicon"),
      description: t("Customize your website's favicon"),
      imageType: "favicon",
      previewUrl: faviconFilePreview?.url,
      url: website?.favicon_url,
      maxImageMegabytes: 0.5,
      allowedFileTypes: ["ico"],
      filePreview: faviconFilePreview,
      setFilePreview: setFaviconFilePreview,
    },
  ];

  function resetFilePreviews() {
    setLogoFilePreview(null);
    setFaviconFilePreview(null);
  }

  function handleCancelChanges() {
    resetFilePreviews();
  }

  async function handleSaveChanges() {
    const res = await UpdateWebsiteBuilderWebsite({
      id: website.id,
      logo: logoFilePreview?.file,
      favicon: faviconFilePreview?.file,
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Failed to update website")));
      return;
    }

    setWebsite(res.data);
    resetFilePreviews();

    dispatch(showSuccess(t("Successfully updated website")));
  }

  async function handleDeleteImage() {
    if (deleteImageModal.imageType == null) {
      return;
    }

    const res = await DeleteWebsiteBuilderWebsiteImage({
      id: website.id,
      image_type: deleteImageModal.imageType,
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Failed to update website")));
      return;
    }

    setWebsite(res.data);
    closeDeleteImageModal();

    dispatch(showSuccess(t("Successfully updated website")));
  }

  function renderUploadCard(uploadCard: ILogoAndBrandUploadCard) {
    const activeUrl = uploadCard.previewUrl ?? uploadCard.url;
    const showDeleteButton = uploadCard.previewUrl == null && uploadCard.url != null;

    return (
      <Card className="website-builder-logo-and-brand-upload-card">
        <Card.Section title={uploadCard.title}>
          <div className="upload-description">{uploadCard.description}</div>
          <Card>
            <Card.Section>
              <div className="upload-content">
                <div className="upload-image-container">
                  {activeUrl != null ? (
                    <img
                      className="upload-image"
                      src={activeUrl}
                    />
                  ) : (
                    <div className="upload-image" />
                  )}
                  {showDeleteButton && (
                    <button
                      type="button"
                      className="upload-image-delete-button"
                      onClick={() => updateDeleteImageModal({ isOpen: true, imageType: uploadCard.imageType })}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
                <WebsiteBuilderFileDropZone
                  theme="light"
                  allowedFileTypes={uploadCard.allowedFileTypes}
                  maxImageMegabytes={uploadCard.maxImageMegabytes}
                  imageDimensionValidations={uploadCard.imageDimensionValidations}
                  filePreview={uploadCard.filePreview}
                  setFilePreview={uploadCard.setFilePreview}
                />
              </div>
            </Card.Section>
          </Card>
        </Card.Section>
      </Card>
    );
  }

  function renderLogoAndBrand() {
    if (websiteLoadingStatus === "succeeded" && websiteExists) {
      return (
        <div>
          {uploadCards.map((uploadCard, index) => <React.Fragment key={index}>{renderUploadCard(uploadCard)}</React.Fragment>)}
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <Page
      narrow
      title={t("Logo & Brand")}
      subtitle={t("Manage your website logo and brand")}
      primaryAction={{
        content:
          <>
            <FontAwesomeIcon icon={faPalette} className="mr-2" />{t("Customize website")}
          </>,
        action: () => history.push("/admin/website-builder-editor"),
        disabled: websiteLoadingStatus !== "succeeded",
      }}
      notificationBarProps={{
        isVisible: changesExist,
        onAction: handleSaveChanges,
        onCancel: handleCancelChanges,
      }}
    >
      <WebsiteBuilderLoadingContent loading={websiteLoadingStatus === "standby" || websiteLoadingStatus === "loading"}>
        {renderLogoAndBrand()}
      </WebsiteBuilderLoadingContent>
      <Portal isMounted={deleteImageModal.isOpen}>
        <Sheet
          title={deleteImageModal.imageType === "logo" ? t("Delete logo?") : t("Delete favicon?")}
          open={deleteImageModal.isOpen}
          size="small"
          closable
          onCancel={closeDeleteImageModal}
          onOk={handleDeleteImage}
          okText={t("Delete")}
        >
          <div>
            {deleteImageModal.imageType === "logo"
              ? t("Are you sure you want to delete your logo?")
              : t("Are you sure you want to delete your favicon?")}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
